
import { pb } from '@/lib/pocketbase';
import { enhancedNotificationService } from './webPushService';
import { ntfyService } from './ntfyService';
import { barkService } from './barkService';

interface NotificationData {
  userId: string;
  type: 'subject_approved' | 'subject_rejected' | 'profile_approved' | 'profile_rejected' | 
        'new_message' | 'message_received' | 
        'booking_confirmed' | 'booking_cancelled' | 'booking_reminder' | 
        'lesson_reminder' | 'lesson_completed' | 
        'tutor_approved' | 'tutor_rejected' | 
        'student_registered' | 
        'payment_received' | 'payment_failed' | 
        'announcement' | 'system_update' | 'general';
  title: string;
  message: string;
  data?: any;
}

export const notificationService = {
  // Send a subject approval notification
  sendSubjectApprovalNotification: async (tutorId: string, subjectName: string) => {
    try {
      await pb.collection('notifications').create({
        userId: tutorId,
        type: 'subject_approved',
        title: 'Subject Approved!',
        message: `Your subject "${subjectName}" has been approved and is now available in your profile.`,
        isRead: false,
        createdAt: new Date().toISOString(),
        data: { subjectName }
      });
    } catch (error) {
      throw error;
    }
  },

  // Send a subject rejection notification
  sendSubjectRejectionNotification: async (tutorId: string, subjectName: string, reason?: string) => {
    try {
      await pb.collection('notifications').create({
        userId: tutorId,
        type: 'subject_rejected',
        title: 'Subject Request Rejected',
        message: `Your subject request for "${subjectName}" has been rejected.${reason ? ` Reason: ${reason}` : ''}`,
        isRead: false,
        createdAt: new Date().toISOString(),
        data: { subjectName, reason }
      });
    } catch (error) {
      throw error;
    }
  },

  // Send a profile approval notification
  sendProfileApprovalNotification: async (tutorId: string) => {
    try {
      await pb.collection('notifications').create({
        userId: tutorId,
        type: 'profile_approved',
        title: 'Profile Approved!',
        message: 'Your tutor profile has been approved and is now live. Students can now book sessions with you.',
        isRead: false,
        createdAt: new Date().toISOString()
      });
    } catch (error) {
      throw error;
    }
  },

  // Send a new message notification (works for both students and tutors)
  sendMessageNotification: async (receiverId: string, senderName: string, senderRole: string = 'user') => {
    console.log('📨 [NOTIFICATION] sendMessageNotification called:', { receiverId, senderName, senderRole });
    try {
      // Send enhanced notification with web push
      // Use unique tag to prevent iOS from replacing notifications
      const uniqueTag = `new-message-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      console.log('📨 [NOTIFICATION] Calling enhancedNotificationService.sendNotificationWithPush with tag:', uniqueTag);
      await enhancedNotificationService.sendNotificationWithPush(
        receiverId,
        {
          title: 'New Message',
          body: `You have a new message from ${senderName}.`,
          icon: '/favicon.ico',
          tag: uniqueTag,
          data: { 
            type: 'new_message',
            senderName, 
            senderRole,
            url: '/messages'
          }
        },
        true // Save to database
      );
      
      // Also send via ntfy (if user has it configured)
      try {
        await ntfyService.sendMessageNotification(receiverId, senderName);
      } catch (ntfyError) {
        console.log('⚠️ [NTFY] Ntfy notification skipped or failed:', ntfyError);
      }
      
      // Also send via Bark (if user has it configured)
      try {
        await barkService.sendMessageNotification(receiverId, senderName);
      } catch (barkError) {
        console.log('⚠️ [BARK] Bark notification skipped or failed:', barkError);
      }
      
      console.log('✅ [NOTIFICATION] sendMessageNotification completed successfully');
    } catch (error) {
      console.error('❌ [NOTIFICATION] sendMessageNotification error:', error);
      throw error;
    }
  },

  // Send a booking confirmation notification (for both students and tutors)
  sendBookingConfirmationNotification: async (userId: string, date: string, otherPartyName: string, isForStudent: boolean = true) => {
    try {
      const message = isForStudent 
        ? `Your session with ${otherPartyName} on ${date} has been confirmed.`
        : `You have a new booking with ${otherPartyName} on ${date}.`;
      
      // Use unique tag to prevent iOS from replacing notifications
      const uniqueTag = `booking-confirmed-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      
      // Send enhanced notification with web push
      await enhancedNotificationService.sendNotificationWithPush(
        userId,
        {
          title: 'Booking Confirmed',
          body: message,
          icon: '/favicon.ico',
          tag: uniqueTag,
          data: { 
            type: 'booking_confirmed',
            date, 
            otherPartyName, 
            isForStudent,
            url: '/schedule'
          }
        },
        true // Save to database
      );
      
      // Also send via ntfy (if user has it configured)
      try {
        await ntfyService.sendBookingNotification(userId, date, otherPartyName, isForStudent);
      } catch (ntfyError) {
        console.log('⚠️ [NTFY] Ntfy notification skipped or failed:', ntfyError);
      }
      
      // Also send via Bark (if user has it configured)
      try {
        await barkService.sendBookingNotification(userId, date, otherPartyName, isForStudent);
      } catch (barkError) {
        console.log('⚠️ [BARK] Bark notification skipped or failed:', barkError);
      }
    } catch (error) {
      throw error;
    }
  },

  // Send a lesson reminder notification
  sendLessonReminderNotification: async (userId: string, lessonTitle: string, startTime: string) => {
    try {
      await pb.collection('notifications').create({
        userId: userId,
        type: 'lesson_reminder',
        title: 'Lesson Reminder',
        message: `Reminder: Your lesson "${lessonTitle}" starts at ${startTime}.`,
        isRead: false,
        createdAt: new Date().toISOString(),
        data: { lessonTitle, startTime }
      });
    } catch (error) {
      throw error;
    }
  },

  // Send announcement to all active users (students and tutors)
  sendAnnouncementToAllUsers: async (announcementTitle: string, announcementContent: string) => {
    try {
      // Get all active users (both students and tutors)
      const users = await pb.collection('users').getList(1, 1000, {
        filter: 'status = "active"'
      });

      // Send notification to each user
      const notificationPromises = users.items.map(user => 
        pb.collection('notifications').create({
          userId: user.id,
          type: 'announcement',
          title: 'New Announcement',
          message: `New announcement: ${announcementTitle}`,
          isRead: false,
          createdAt: new Date().toISOString(),
          data: { announcementTitle, announcementContent }
        })
      );

      await Promise.all(notificationPromises);
    } catch (error) {
      throw error;
    }
  },

  // Generic notification sender
  sendNotification: async (notificationData: NotificationData) => {
    try {
      await pb.collection('notifications').create({
        ...notificationData,
        isRead: false,
        createdAt: new Date().toISOString()
      });
    } catch (error) {
      throw error;
    }
  }
};
